import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { FormNavigationProvider, NavTextField } from '../components/FormNavigation';
import { CloudDownload, CloudUpload, Delete } from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import { apiService } from '../services/api';

type Status = { severity: 'success' | 'error'; message: string } | null;

const Storage: React.FC = () => {
  // Upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadKey, setUploadKey] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<Status>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const uploadContainerRef = useRef<HTMLDivElement | null>(null);

  // Download
  const [downloadKey, setDownloadKey] = useState('');
  const [downloadStatus, setDownloadStatus] = useState<Status>(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const downloadContainerRef = useRef<HTMLDivElement | null>(null);

  // Delete
  const [deleteKey, setDeleteKey] = useState('');
  const [deleteStatus, setDeleteStatus] = useState<Status>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const deleteContainerRef = useRef<HTMLDivElement | null>(null);

  const handleUpload = async () => {
    if (!selectedFile || !uploadKey.trim()) return;
    setUploadLoading(true);
    setUploadStatus(null);
    try {
      const res = await apiService.getUploadUrl(uploadKey.trim(), selectedFile.type || undefined);
      await fetch(res.data.url, {
        method: 'PUT',
        body: selectedFile,
        headers: selectedFile.type ? { 'Content-Type': selectedFile.type } : {},
      });
      setUploadStatus({ severity: 'success', message: `Uploaded → ${res.data.key}` });
      setSelectedFile(null);
      setUploadKey('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: any) {
      setUploadStatus({ severity: 'error', message: err.message || 'Upload failed' });
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!downloadKey.trim()) return;
    setDownloadLoading(true);
    setDownloadStatus(null);
    try {
      const res = await apiService.getDownloadUrl(downloadKey.trim());
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
      setDownloadStatus({ severity: 'success', message: `Presigned URL opened for: ${res.data.key}` });
    } catch (err: any) {
      setDownloadStatus({ severity: 'error', message: err.message || 'Download failed' });
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteKey.trim()) return;
    setDeleteLoading(true);
    setDeleteStatus(null);
    try {
      const res = await apiService.deleteStorageObject(deleteKey.trim());
      setDeleteStatus({ severity: 'success', message: `Deleted: ${res.data.key}` });
      setDeleteKey('');
    } catch (err: any) {
      setDeleteStatus({ severity: 'error', message: err.message || 'Delete failed' });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <Box>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          Storage
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Test interface for S3 storage — keys are automatically scoped to your session's tenant/solution/unit prefix.
        </Typography>

        <Stack spacing={3}>
          {/* Upload */}
          <Paper ref={uploadContainerRef} variant="outlined" sx={{ p: 3 }}>
            <FormNavigationProvider containerRef={uploadContainerRef}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <CloudUpload color="primary" />
                <Typography variant="h6" fontWeight={600}>Upload</Typography>
              </Stack>
              <Stack spacing={2}>
                <NavTextField
                  label="Key (relative path)"
                  placeholder="e.g. reports/2026-03/file.csv"
                  value={uploadKey}
                  onChange={(e) => setUploadKey(e.target.value)}
                  size="small"
                  fullWidth
                />
                <Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                  />
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Button variant="outlined" size="small" onClick={() => fileInputRef.current?.click()}>
                      Choose file
                    </Button>
                    {selectedFile && (
                      <Typography variant="body2" color="text.secondary">
                        {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </Typography>
                    )}
                  </Stack>
                </Box>
                <Box>
                  <Button
                    variant="contained"
                    startIcon={uploadLoading ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
                    onClick={handleUpload}
                    disabled={uploadLoading || !selectedFile || !uploadKey.trim()}
                  >
                    Upload
                  </Button>
                </Box>
                {uploadStatus && <Alert severity={uploadStatus.severity}>{uploadStatus.message}</Alert>}
              </Stack>
            </FormNavigationProvider>
          </Paper>

          <Divider />

          {/* Download */}
          <Paper ref={downloadContainerRef} variant="outlined" sx={{ p: 3 }}>
            <FormNavigationProvider containerRef={downloadContainerRef}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <CloudDownload color="primary" />
                <Typography variant="h6" fontWeight={600}>Download</Typography>
              </Stack>
              <Stack spacing={2}>
                <NavTextField
                  label="Key (relative path)"
                  placeholder="e.g. reports/2026-03/file.csv"
                  value={downloadKey}
                  onChange={(e) => setDownloadKey(e.target.value)}
                  size="small"
                  fullWidth
                />
                <Box>
                  <Button
                    variant="contained"
                    startIcon={downloadLoading ? <CircularProgress size={16} color="inherit" /> : <CloudDownload />}
                    onClick={handleDownload}
                    disabled={downloadLoading || !downloadKey.trim()}
                  >
                    Get download URL
                  </Button>
                </Box>
                {downloadStatus && <Alert severity={downloadStatus.severity}>{downloadStatus.message}</Alert>}
              </Stack>
            </FormNavigationProvider>
          </Paper>

          <Divider />

          {/* Delete */}
          <Paper ref={deleteContainerRef} variant="outlined" sx={{ p: 3 }}>
            <FormNavigationProvider containerRef={deleteContainerRef}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <Delete color="error" />
                <Typography variant="h6" fontWeight={600}>Delete</Typography>
              </Stack>
              <Stack spacing={2}>
                <NavTextField
                  label="Key (relative path)"
                  placeholder="e.g. reports/2026-03/file.csv"
                  value={deleteKey}
                  onChange={(e) => setDeleteKey(e.target.value)}
                  size="small"
                  fullWidth
                />
                <Box>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : <Delete />}
                    onClick={handleDelete}
                    disabled={deleteLoading || !deleteKey.trim()}
                  >
                    Delete object
                  </Button>
                </Box>
                {deleteStatus && <Alert severity={deleteStatus.severity}>{deleteStatus.message}</Alert>}
              </Stack>
            </FormNavigationProvider>
          </Paper>
        </Stack>
      </Box>
    </DashboardLayout>
  );
};

export default Storage;
